import React, { useContext, useState } from 'react'
import { DataCard } from '@/components/DataCard/DataCard'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useApiClient } from '@/utils/api/ApiClient'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { persistor } from 'store/store'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { WebSocketContext } from 'utils/WebSocketContext'

export const UserProfileSessionPage: React.FC = () => {
  const [_, setAuth] = useContext(AuthSessionContext) // eslint-disable-line @typescript-eslint/no-unused-vars
  const { hub } = useContext(WebSocketContext) // eslint-disable-line @typescript-eslint/no-unused-vars
  const identityApiClient = useApiClient(IdentityApiClient)

  const [loading, setLoading] = useState(false)
  const setToast = useContext(ToastContext)
  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(
    () => identityApiClient.getUserActiveSessions(),
    []
  )
  const { data, isLoading } = useFetchOne(fetchData)

  const logoutFromSession = async (sessionId?: string) => {
    setLoading(true)
    try {
      if (!sessionId) {
        await identityApiClient.signOut()

        persistor.purge()

        setAuth(null)
      } else {
        if (data && data.items.length === 1) {
          await identityApiClient.signOut(sessionId)
          hub?.client.stop()

          return persistor.purge()
        }
        await identityApiClient.signOut(sessionId)
        hub?.client.stop()
        setAuth(null)
        forceRefresh()
        setToast(successToast(`Session ${sessionId} removed.`))
      }
    } finally {
      setLoading(false)
    }
  }

  if (!data) return null

  return (
    <DataCard title='Sessions'>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>Browser</TableHead>
            <TableHead>IP</TableHead>
            <TableHead>Platform</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          <Loading isLoading={isLoading}>
            {data?.items.map((session) => (
              <TableRow key={session.sessionId}>
                <TableCell>{session.sessionId}</TableCell>
                <TableCell>{session.client}</TableCell>
                <TableCell>{session.browser}</TableCell>
                <TableCell>{session.ipAddress}</TableCell>
                <TableCell>{session.platform}</TableCell>
                <TableCell align='center'>
                  <MoreButton
                    items={[
                      {
                        name: 'Remove',
                        onClick: () => logoutFromSession(session.sessionId),
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
          </Loading>
        </TableBody>
      </Table>

      <Button
        className='mx-auto'
        disabled={loading}
        variant='destructive'
        onClick={() => logoutFromSession()}
      >
        Remove all
        {loading && <Spinner />}
      </Button>
    </DataCard>
  )
}
